import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Header from "../components/header/Header";
import Footer from "../components/footer";
import Gotop from "../components/gotop";
import Icon from "../components/brand/Icon";
import { positionAPI } from "../services/api";
import { getJobLocation } from "../utils/jobDisplay";
import * as analytics from "../utils/analytics";
import "./Home.scss";

const POPULAR_ROLES = [".NET Engineer", "Data Engineer", "Cloud Architect", "AI Engineer"];

const ROLE_CATEGORIES = [
  { icon: "code", title: "Software Engineering", search: "Engineer", blurb: "Backend, frontend and platform roles" },
  { icon: "brain", title: "AI & Machine Learning", search: "AI", blurb: "ML engineers, LLM and applied AI" },
  { icon: "cloud", title: "Cloud & DevOps", search: "Cloud", blurb: "AWS, Azure, Kubernetes, SRE" },
  { icon: "database", title: "Data Engineering", search: "Data", blurb: "Pipelines, warehouses, analytics" },
  { icon: "layers", title: "Full Stack", search: "Full Stack", blurb: "React, Node, .NET and more" },
  { icon: "headset", title: "Virtual Assistants", search: "Assistant", blurb: "Executive, support and operations" },
  { icon: "handshake", title: "Account Management", search: "Account", blurb: "Client success and delivery" },
  { icon: "compass", title: "Product Management", search: "Product", blurb: "Product owners and managers" },
];

const STEPS = [
  { title: "We post verified roles", text: "Real positions from US companies, reviewed by our team before they go live." },
  { title: "You apply for free", text: "Browse openings and apply in minutes. No fees, no salary cuts." },
  { title: "We review every applicant", text: "Our recruiters assess your experience and match you to the right role." },
  { title: "Shortlists go to clients", text: "The strongest candidates are introduced directly to hiring managers." },
  { title: "Offer and onboarding", text: "Companies make the call and we help you get started." },
];

const AUDIENCES = [
  {
    icon: "code",
    title: "Engineers & developers",
    items: ["Full-stack developers", "DevOps engineers", "QA specialists", "Mobile developers", "Data scientists"],
  },
  {
    icon: "headset",
    title: "Virtual assistants",
    items: ["Executive assistants", "Customer support", "Data entry experts", "Social media managers", "Bookkeepers"],
  },
  {
    icon: "building",
    title: "US companies",
    items: ["Startups", "Small and mid-sized businesses", "Remote-first companies", "Agencies hiring offshore", "Enterprise teams"],
  },
];

const COMPARISON = [
  { name: "Dice", logo: "dice.png", type: "Job board", price: "Free / Paid", login: "Yes", screening: "No", direct: "No" },
  { name: "LinkedIn", logo: "linkedin.png", type: "Professional network", price: "Free / Premium", login: "Yes", screening: "No", direct: "Limited" },
  { name: "Glassdoor", logo: "glassdoor.png", type: "Company reviews", price: "Free", login: "Yes", screening: "No", direct: "No" },
  { name: "Indeed", logo: "indeed.png", type: "Job aggregator", price: "Free", login: "Optional", screening: "No", direct: "No" },
  { name: "Upwork", logo: "upwork.png", type: "Freelance marketplace", price: "Commission fees", login: "Yes", screening: "Basic", direct: "Yes" },
  { name: "Hubstaff Talent", logo: "hubstafftalent.png", type: "Freelancer directory", price: "Free", login: "Yes", screening: "No", direct: "Yes" },
];

function Cell({ value }) {
  if (value === "Yes") return <span className="ts-compare__yes"><Icon name="check" size={16} strokeWidth={2.4} /> Yes</span>;
  if (value === "No") return <span className="ts-compare__no">No</span>;
  return <span>{value}</span>;
}

function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchData, setSearchData] = useState({ role: "", location: "" });
  const [latestJobs, setLatestJobs] = useState([]);
  const [totalJobs, setTotalJobs] = useState(null);
  const [contactSent, setContactSent] = useState(false);

  const handleSearch = (e) => {
    e.preventDefault();
    analytics.trackEvent("hero_search_submit", {
      search_term: searchData.role || undefined,
      location: searchData.location || undefined,
    });
    const queryParams = new URLSearchParams();
    if (searchData.role) queryParams.append("search", searchData.role);
    if (searchData.location) queryParams.append("location", searchData.location);
    navigate(`/careers?${queryParams.toString()}`);
  };

  const goToCareers = (ctaName, search) => {
    analytics.trackEvent("cta_click", { cta_name: ctaName, destination: "/careers" });
    navigate(search ? `/careers?search=${encodeURIComponent(search)}` : "/careers");
  };

  // Latest published positions for the hero panel
  useEffect(() => {
    let active = true;
    positionAPI
      .getAll({ page: 1, limit: 3 })
      .then((res) => {
        if (!active || !res.data?.success) return;
        setLatestJobs((res.data.data || []).slice(0, 3));
        setTotalJobs(res.data.pagination?.total ?? null);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  // Scroll to section if hash is present in URL
  useEffect(() => {
    const id = location.hash.replace("#", "");
    if (!id) return;
    const t = setTimeout(() => {
      const section = document.getElementById(id);
      if (section) section.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => clearTimeout(t);
  }, [location]);

  useEffect(() => {
    document.title = "TalSolution – Connect Global Talent with US Opportunities";
    return () => {
      document.title = "TalSolution";
    };
  }, []);

  return (
    <div className="ts-home">
      <Header />

      {/* Hero */}
      <section className="ts-hero">
        <div className="ts-hero__glow" aria-hidden="true" />
        <div className="tf-container">
          <div className="ts-hero__grid">
            <div className="ts-hero__copy">
              <span className="ts-eyebrow ts-eyebrow--dark">
                <span className="ts-eyebrow__dot" /> Free for candidates · Vetted US roles
              </span>
              <h1 className="ts-hero__title">
                Global talent, <span className="ts-gradient-text">hired by US teams.</span>
              </h1>
              <p className="ts-hero__subtitle">
                TalSolution connects software engineers, IT specialists and virtual assistants with contract and
                full-time roles at US companies. We screen every applicant, so hiring managers meet the right people
                first.
              </p>

              <form className="ts-search" onSubmit={handleSearch} role="search">
                <label className="ts-search__field">
                  <Icon name="search" size={20} />
                  <span className="visually-hidden">Role</span>
                  <input
                    id="role"
                    type="text"
                    placeholder="Role or skill"
                    value={searchData.role}
                    onChange={(e) => setSearchData({ ...searchData, role: e.target.value })}
                  />
                </label>
                <span className="ts-search__divider" aria-hidden="true" />
                <label className="ts-search__field">
                  <Icon name="pin" size={20} />
                  <span className="visually-hidden">Location</span>
                  <input
                    id="location"
                    type="text"
                    placeholder="Location or Remote"
                    value={searchData.location}
                    onChange={(e) => setSearchData({ ...searchData, location: e.target.value })}
                  />
                </label>
                <button type="submit" className="ts-btn ts-btn--primary ts-search__submit">
                  Search jobs
                </button>
              </form>

              <div className="ts-hero__popular">
                <span>Popular:</span>
                {POPULAR_ROLES.map((role) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => {
                      analytics.trackEvent("popular_role_click", { role });
                      setSearchData({ ...searchData, role });
                    }}
                  >
                    {role}
                  </button>
                ))}
              </div>
            </div>

            <aside className="ts-hero__panel" aria-label="Latest openings">
              <div className="ts-panel">
                <div className="ts-panel__head">
                  <span className="ts-panel__title">Latest openings</span>
                  {totalJobs ? <span className="ts-panel__badge">{totalJobs} open</span> : null}
                </div>
                {latestJobs.length > 0 ? (
                  <ul className="ts-panel__list">
                    {latestJobs.map((job) => (
                      <li key={job._id}>
                        <Link to={`/careers/${job._id}`} className="ts-panel__job">
                          <span className="ts-panel__avatar">{(job.title || "?").trim().charAt(0).toUpperCase()}</span>
                          <span className="ts-panel__meta">
                            <strong>{job.title}</strong>
                            <small>
                              {[job.employmentType, (job.workType || [])[0], getJobLocation(job)].filter(Boolean).join(" · ")}
                            </small>
                          </span>
                          <Icon name="arrow" size={18} className="ts-panel__arrow" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <ul className="ts-panel__list">
                    {ROLE_CATEGORIES.slice(0, 3).map((c) => (
                      <li key={c.title}>
                        <Link to={`/careers?search=${encodeURIComponent(c.search)}`} className="ts-panel__job">
                          <span className="ts-panel__avatar"><Icon name={c.icon} size={20} /></span>
                          <span className="ts-panel__meta">
                            <strong>{c.title}</strong>
                            <small>{c.blurb}</small>
                          </span>
                          <Icon name="arrow" size={18} className="ts-panel__arrow" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
                <button type="button" className="ts-panel__all" onClick={() => goToCareers("hero_panel_all")}>
                  View all open roles <Icon name="arrow" size={16} />
                </button>
              </div>
            </aside>
          </div>

          <ul className="ts-trust">
            <li><Icon name="globe" size={20} /> Global talent pool</li>
            <li><Icon name="briefcase" size={20} /> US-based opportunities</li>
            <li><Icon name="clock" size={20} /> Contract & full-time</li>
            <li><Icon name="wallet" size={20} /> 100% free for candidates</li>
          </ul>
        </div>
      </section>

      {/* Roles */}
      <section className="ts-section">
        <div className="tf-container">
          <div className="ts-section__head ts-section__head--split">
            <div>
              <span className="ts-eyebrow">Open roles</span>
              <h2 className="ts-section__title">Roles we hire for</h2>
              <p className="ts-section__lead">From senior engineers to executive assistants, we place professionals across eight core tracks.</p>
            </div>
            <button type="button" className="ts-btn ts-btn--ghost" onClick={() => goToCareers("see_jobs_hero")}>
              See all jobs <Icon name="arrow" size={18} />
            </button>
          </div>
          <div className="ts-roles">
            {ROLE_CATEGORIES.map((c) => (
              <button
                key={c.title}
                type="button"
                className="ts-role"
                onClick={() => {
                  analytics.trackEvent("job_category_click", { category_title: c.title });
                  navigate(`/careers?search=${encodeURIComponent(c.search)}`);
                }}
              >
                <span className="ts-role__icon"><Icon name={c.icon} size={22} /></span>
                <span className="ts-role__title">{c.title}</span>
                <span className="ts-role__blurb">{c.blurb}</span>
                <span className="ts-role__link">Explore <Icon name="arrow" size={16} /></span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* What is TalSolution */}
      <section id="what-is-talsolution" className="ts-section ts-section--tint">
        <div className="tf-container">
          <div className="ts-about">
            <div className="ts-about__copy">
              <span className="ts-eyebrow">About TalSolution</span>
              <h2 className="ts-section__title">A hiring network built for global professionals</h2>
              <p className="ts-section__lead">
                TalSolution simplifies how US companies find and work with talent abroad. We focus on tech hiring,
                remote roles and virtual assistance, giving companies access to world-class people and giving
                professionals fair access to US opportunities.
              </p>
              <div className="ts-about__actions">
                <button type="button" className="ts-btn ts-btn--primary" onClick={() => goToCareers("about_browse")}>
                  Browse open roles
                </button>
                <a href="#contact-section" className="ts-btn ts-btn--ghost">Talk to us</a>
              </div>
            </div>
            <div className="ts-about__cards">
              <article className="ts-card">
                <span className="ts-card__icon"><Icon name="users" size={22} /></span>
                <h3>For candidates</h3>
                <ul className="ts-checklist">
                  <li>Verified US job opportunities</li>
                  <li>Apply directly, with no fees</li>
                  <li>Reach real hiring managers</li>
                  <li>Build a global professional profile</li>
                </ul>
              </article>
              <article className="ts-card">
                <span className="ts-card__icon ts-card__icon--lime"><Icon name="building" size={22} /></span>
                <h3>For companies</h3>
                <ul className="ts-checklist">
                  <li>Hire without expensive recruiters</li>
                  <li>Pre-screened international talent</li>
                  <li>Contract, remote or full-time</li>
                  <li>Faster hiring decisions</li>
                </ul>
              </article>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="ts-section">
        <div className="tf-container">
          <div className="ts-section__head ts-section__head--center">
            <span className="ts-eyebrow">How it works</span>
            <h2 className="ts-section__title">From application to offer in five steps</h2>
            <p className="ts-section__lead">A simple, transparent process that connects global talent with US opportunities.</p>
          </div>
          <ol className="ts-steps">
            {STEPS.map((step, i) => (
              <li key={step.title} className="ts-step">
                <span className="ts-step__num">{String(i + 1).padStart(2, "0")}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Why free */}
      <section className="ts-section ts-section--navy">
        <div className="tf-container">
          <div className="ts-free">
            <div className="ts-free__copy">
              <span className="ts-eyebrow ts-eyebrow--dark">Always free</span>
              <h2 className="ts-section__title">Why TalSolution is 100% free for candidates</h2>
              <p className="ts-section__lead">
                Access to global opportunities shouldn't depend on geography or money. We never charge candidates or
                take a commission from their salary.
              </p>
            </div>
            <div className="ts-free__grid">
              <div className="ts-free__item">
                <Icon name="globe" size={22} />
                <h3>Open doors for global professionals</h3>
                <p>Skilled people outside the US struggle to find legitimate, well-paid remote roles. We exist to change that.</p>
              </div>
              <div className="ts-free__item">
                <Icon name="zap" size={22} />
                <h3>Help US companies hire faster</h3>
                <p>Companies get a trusted talent pool without inflated recruitment fees or middlemen.</p>
              </div>
              <div className="ts-free__item">
                <Icon name="shield" size={22} />
                <h3>Build long-term relationships</h3>
                <p>Our goal isn't transactions. It's trust and long-term collaboration on both sides.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="ts-section">
        <div className="tf-container">
          <div className="ts-section__head ts-section__head--center">
            <span className="ts-eyebrow">Who it's for</span>
            <h2 className="ts-section__title">Who can use TalSolution?</h2>
            <p className="ts-section__lead">Whether you're building the future or scaling your operations, we'll find the right match.</p>
          </div>
          <div className="ts-audiences">
            {AUDIENCES.map((a) => (
              <article key={a.title} className="ts-card ts-card--audience">
                <span className="ts-card__icon"><Icon name={a.icon} size={22} /></span>
                <h3>{a.title}</h3>
                <ul className="ts-checklist">
                  {a.items.map((item) => <li key={item}>{item}</li>)}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="ts-section ts-section--tint">
        <div className="tf-container">
          <div className="ts-section__head ts-section__head--center">
            <span className="ts-eyebrow">Why us</span>
            <h2 className="ts-section__title">How TalSolution is different</h2>
            <p className="ts-section__lead">We don't just list jobs. We help make hiring decisions easier.</p>
          </div>
          <div className="ts-compare" role="region" aria-label="Platform comparison" tabIndex={0}>
            <table>
              <thead>
                <tr>
                  <th scope="col">Platform</th>
                  <th scope="col">Type</th>
                  <th scope="col">Price</th>
                  <th scope="col">Login required</th>
                  <th scope="col">Pre-screening</th>
                  <th scope="col">Direct hiring</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row) => (
                  <tr key={row.name}>
                    <th scope="row">
                      <img src={require(`../assets/images/logos/${row.logo}`)} alt="" />
                      {row.name}
                    </th>
                    <td>{row.type}</td>
                    <td>{row.price}</td>
                    <td>{row.login}</td>
                    <td><Cell value={row.screening} /></td>
                    <td><Cell value={row.direct} /></td>
                  </tr>
                ))}
                <tr className="ts-compare__us">
                  <th scope="row">
                    <img src={require("../assets/images/brand/talsolution-mark.png")} alt="" />
                    TalSolution
                  </th>
                  <td>Curated network + agency</td>
                  <td><strong>100% free</strong></td>
                  <td>No</td>
                  <td><Cell value="Yes" /></td>
                  <td><Cell value="Yes" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ts-section ts-section--cta">
        <div className="tf-container">
          <div className="ts-cta">
            <div>
              <h2>Join the TalSolution network</h2>
              <p>Free for candidates. No salary cuts. No hidden fees.</p>
            </div>
            <div className="ts-cta__actions">
              <button
                type="button"
                className="ts-btn ts-btn--lime"
                onClick={() => {
                  analytics.trackEvent("cta_click", { cta_name: "create_profile", destination: "/register" });
                  navigate("/register");
                }}
              >
                Create your free profile
              </button>
              <button type="button" className="ts-btn ts-btn--on-dark" onClick={() => goToCareers("search_open_jobs")}>
                Search open jobs
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact-section" className="ts-section">
        <div className="tf-container">
          <div className="ts-contact">
            <div className="ts-contact__info">
              <span className="ts-eyebrow">Contact</span>
              <h2 className="ts-section__title">Let's talk</h2>
              <p className="ts-section__lead">
                Whether you're a candidate looking for your next role or a company looking for global talent, we're
                ready to help.
              </p>
              <ul className="ts-contact__list">
                <li>
                  <span className="ts-card__icon"><Icon name="phone" size={20} /></span>
                  <div><small>Call us</small><a href="tel:+12108302282">+1 (210) 830-2282</a></div>
                </li>
                <li>
                  <span className="ts-card__icon"><Icon name="mail" size={20} /></span>
                  <div><small>Email</small><a href="mailto:info@talsolution.com">info@talsolution.com</a></div>
                </li>
                <li>
                  <span className="ts-card__icon"><Icon name="pin" size={20} /></span>
                  <div><small>Office</small><span>2821 Mexico Gravel Rd #5, Columbia MO 65202</span></div>
                </li>
              </ul>
            </div>
            <form
              className="ts-contact__form"
              onSubmit={(e) => {
                e.preventDefault();
                analytics.trackEvent("contact_form_submit", { form_name: "home_contact" });
                // No contact API yet: open the visitor's mail app with the message filled in.
                const f = new FormData(e.currentTarget);
                const body = `${f.get("message")}\n\n${f.get("name")} <${f.get("email")}>`;
                window.location.href = `mailto:info@talsolution.com?subject=${encodeURIComponent(f.get("subject"))}&body=${encodeURIComponent(body)}`;
                setContactSent(true);
              }}
            >
              <div className="ts-field-row">
                <label className="ts-field">
                  <span>Name</span>
                  <input name="name" type="text" placeholder="Your name" required />
                </label>
                <label className="ts-field">
                  <span>Email</span>
                  <input name="email" type="email" placeholder="you@company.com" required />
                </label>
              </div>
              <label className="ts-field">
                <span>Subject</span>
                <input name="subject" type="text" placeholder="How can we help?" required />
              </label>
              <label className="ts-field">
                <span>Message</span>
                <textarea name="message" placeholder="Tell us a little about what you need" rows="5" required></textarea>
              </label>
              {contactSent ? (
                <p className="ts-contact__sent" role="status">
                  Your email app should open with this message. If it doesn't, write to us at{" "}
                  <a href="mailto:info@talsolution.com">info@talsolution.com</a>.
                </p>
              ) : null}
              <button type="submit" className="ts-btn ts-btn--primary ts-btn--block">Send message</button>
            </form>
          </div>
        </div>
      </section>

      <Footer />
      <Gotop />
    </div>
  );
}

export default Home;
